import React, {useEffect} from 'react';
import Toolbar from './Toolbar';
import FlagColorToolbar from './FlagColorToolbar/FlagColorToolbar';
import {COMMON_BUILDING_CONFIGS, KEY_POINT_CONFIGS, TERRAIN_CONFIGS} from '../../configs/tokens';
import {useSelector} from 'react-redux';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectGoblinsTraps,
  selectHexes,
  selectIsGameStarted,
  selectPendingMeleeDamage,
  selectPendingShootingActions,
  selectPhase,
  selectPlayers,
  selectSelectedUnit,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage
} from '../../redux/reducers/sharedStateReducer/selectors';
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import WarRecruitmentStageToolbar from "./NewUnitToolbar/WarRecruitmentStageToolbar";
import NewBuildingToolbar from "./NewBuildingToolbar/NewBuildingToolbar";
import PlanningBuildingToolbar from "./PlanningBuildingToolbar/PlanningBuildingToolbar";
import {checkIfConstructionPlanningAvailable} from "../../utils/gameState/construction/planning/getConstructionPlanContext";
import {checkIfCentralQuarter} from "../../utils/buildings/checkIfCentralQuarter";
import UNIT_TYPES from "../../configs/units";
import {iterateObjectPropValues} from "../../utils/other/iterateObjectPropValues";
import SelectedUnitShooting from "./SelectedUnitShooting/SelectedUnitShooting";
import SelectedUnitMelee from "./SelectedUnitMelee/SelectedUnitMelee";
import {
  selectInspectedHexKey,
  selectIsTutorialActive,
  selectShowMeleeTargets
} from "../../redux/reducers/privateStateReducer/selectors";
import InspectedCellPanel from "./InspectedCellPanel/InspectedCellPanel";
import {useIsCurrentUserTurn} from "../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import LeprechaunsMovementToolbar from "./factions/leprechauns/LeprechaunsMovementToolbar/LeprechaunsMovementToolbar";
import {checkIfLeprechaunsUnitCouldSteal} from "../../utils/factions/checkIfLeprechaunsUnitCouldSteal";
import {FACTIONS} from "../../constants/factions";
import {isUnitFaction} from "../../utils/factions/isUnitFaction";
import NeutralUnitsToolbar from "./NeutralUnitsToolbar";
import MissionEditorToolbars from "./MissionEditorToolbars/MissionEditorToolbars";
import {isPlayerPolicy} from "../../utils/factions/isPlayerPolicy";
import {POLICIES} from "../../constants/policies";
import PolicyAgrarianismToolbar from "./policies/PolicyAgrarianismToolbar/PolicyAgrarianismToolbar";
import RatmenRecruitmentToolbar from "./factions/ratmen/RatmenRecruitmentToolbar/RatmenRecruitmentToolbar";
import SupplyPanel from "./SupplyPanel/SupplyPanel";
import GiantsSupplyToolbar from "./factions/giants/GiantsSupplyToolbar/GiantsSupplyToolbar";
import {isPlayerFaction} from "../../utils/factions/isPlayerFaction";
import SuccubesAbilitiesToolbar from "./factions/succubes/SuccubesAbilitiesToolbar/SuccubesAbilitiesToolbar";
import {checkIfSuccubesUnitCouldSeduce} from "../../utils/factions/checkIfSuccubesUnitCouldSeduce";
import SelectedUnitSeduction from "./SelectedUnitSeduction/SelectedUnitSeduction";
import {checkIfPlayerHasUnitOfFaction} from "../../utils/factions/checkIfPlayerHasUnitOfFaction";
import GoblinsAbilitiesToolbar from "./factions/goblins/GoblinsAbilitiesToolbar/GoblinsAbilitiesToolbar";
import {checkIfGoblinsUnitCouldUseAbility} from "../../utils/factions/checkIfGoblinsUnitCouldUseAbility";
import SelectedUnitGoblinsAbility from "./SelectedUnitGoblinsAbility/SelectedUnitGoblinsAbility";
import {selectSupplyActingPlayer} from "../../utils/gameState/supply/parallelSupplySelectors";
import {useIsAbilityResultHeld} from "../../utils/effects/useIsAbilityResultHeld";

/**
 * @param {Object} props
 * @param {Function} props.setIsContentEmpty
 * @param {boolean} [props.isShootingResultRevealed] — можно ли показывать
 *   результат стрельбы выбранного отряда: панель открывается с попаданием
 *   залпа, а не с выстрелом (решает футер, см. useShootingResultRevealed)
 * @param {boolean} [props.isMeleeResultRevealed] — можно ли показывать
 *   расчет урона ближнего боя выбранного отряда: панель открывается после
 *   ударов на поле (решает футер, см. useMeleeResultRevealed)
 */
function TokensToolbar({setIsContentEmpty, isShootingResultRevealed = true, isMeleeResultRevealed = true}) {
  const isGameStarted = useSelector(selectIsGameStarted);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const players = useSelector(selectPlayers);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const selectedUnit = useSelector(selectSelectedUnit);
  const units = useSelector(selectUnits);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  const hexes = useSelector(selectHexes);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const fortressesUnderConstruction = useSelector(selectFortressesUnderConstruction);
  const citiesCache = useSelector(selectCitiesCache);
  const goblinsTraps = useSelector(selectGoblinsTraps);
  const showMeleeTargets = useSelector(selectShowMeleeTargets);
  const inspectedHexKey = useSelector(selectInspectedHexKey);
  const isTutorialActive = useSelector(selectIsTutorialActive);
  const isConstructionPlanning = useSelector(checkIfConstructionPlanningAvailable);
  // Игрок, за которого клиент раздает снабжение: в одновременной фазе
  // снабжения сетевой игры — свой, пока не закончил (иначе null), в
  // поочередной — текущий (см. selectSupplyActingPlayer)
  const supplyPlayer = useSelector(selectSupplyActingPlayer);
  // Сердце соблазна или бомба гоблина выбранного отряда еще летят: формула
  // броска откроется вместе с итогом на поле (см. abilityResultHolds)
  const isSelectedUnitAbilityResultHeld = useIsAbilityResultHeld(selectedUnit?.id);

  const availableBuildings = COMMON_BUILDING_CONFIGS.filter(building => {
    if (checkIfCentralQuarter(building.id)) {
      return building.id === 'central_quarter_neutral';
    } else {
      return true;
    }
  });

  let content;
  if (isGameStarted && inspectedHexKey) {
    // Просмотр информации о выбранной ячейке (см. handleInspectionClick в
    // GameScene) — работает и в свой ход, приоритетнее фазового контента;
    // клик по ячейке с доступным действием фазы снимает выбор автоматически
    content = <InspectedCellPanel/>;
  } else if (!isGameStarted) {
    const neutralUnits = []
    iterateObjectPropValues(UNIT_TYPES, (value) => {
      neutralUnits.push({
        ...value,
        image: `/img/players/units/Neutral unit d${value.maxSize}.png`,
      })
    })
    content = <>
      {/*Стартовый флаг игрока: один инструмент + палитра всех цветов PLAYER_COLORS*/}
      <FlagColorToolbar/>
      {/*Ключевые точки*/}
      <Toolbar tokens={KEY_POINT_CONFIGS}/>
      {/*Ландшафт*/}
      <Toolbar tokens={TERRAIN_CONFIGS}/>
      {/*Строения*/}
      <Toolbar tokens={availableBuildings}/>
      {/*Нейтральные отряды*/}
      <NeutralUnitsToolbar tokens={neutralUnits}/>
      {/*Редактор миссий: центральные кварталы и отряды конкретных игроков*/}
      <MissionEditorToolbars/>
    </>;
  } else if (phase === PHASES.SUPPLY) {
    // Тулбары фазы считаются по фракции и политике игрока, раздающего
    // снабжение, а не замороженного текущего игрока одновременной фазы
    if (isCurrentUserTurn && supplyPlayer) {
      const isCurrentPlayerPolicyAgrarianism = isPlayerPolicy(supplyPlayer, POLICIES.AGRARIANISM);

      let factionSupplyToolbar = null;
      if (isPlayerFaction(supplyPlayer, FACTIONS.RATMEN)) {
        factionSupplyToolbar = <RatmenRecruitmentToolbar withAgrarianism={isCurrentPlayerPolicyAgrarianism}/>
      } else if (checkIfPlayerHasUnitOfFaction(supplyPlayer, FACTIONS.GIANTS, units)) {
        factionSupplyToolbar = <GiantsSupplyToolbar withAgrarianism={isCurrentPlayerPolicyAgrarianism}/>
      }

      const supplyActions = (factionSupplyToolbar || isCurrentPlayerPolicyAgrarianism) && <>
        {factionSupplyToolbar}
        {isCurrentPlayerPolicyAgrarianism && <PolicyAgrarianismToolbar/>}
      </>;

      // Панель раздачи со сводкой и сбросом — всем в свой ход, независимо от
      // фракции и политик. В обучении ее нет: снабжение там раздается по
      // сценарию главы
      if (isTutorialActive) {
        content = supplyActions || undefined;
      } else {
        content = <SupplyPanel>{supplyActions}</SupplyPanel>;
      }
    }
  } else if (phase === PHASES.CONSTRUCTION) {
    if (isCurrentUserTurn) {
      content = <NewBuildingToolbar/>;
    } else if (isConstructionPlanning) {
      // План строительства в чужой ход — своя панель без действий на сервер
      content = <PlanningBuildingToolbar/>;
    }
  } else if (phase === PHASES.WAR) {
    switch (warPhaseStage) {
      case WAR_PHASE_STAGES.RECRUITMENT:
        if (isCurrentUserTurn) {
          content = <WarRecruitmentStageToolbar/>;
        }
        break;
      case WAR_PHASE_STAGES.MOVEMENT:
        const otherPlayers = players.filter(player => player.id !== currentPlayer.id && player.coins > 0);

        if (isCurrentUserTurn
          && selectedUnit && isUnitFaction(selectedUnit, FACTIONS.LEPRECHAUNS)
          && checkIfLeprechaunsUnitCouldSteal(selectedUnit, otherPlayers)
        ) {
          content = <LeprechaunsMovementToolbar/>
        }
        break;
      case WAR_PHASE_STAGES.ABILITIES:
        if (isSelectedUnitAbilityResultHeld) {
          content = null;
        } else if (selectedUnit && isUnitFaction(selectedUnit, FACTIONS.SUCCUBES) && selectedUnit.hasUsedAbilityThisWarPhase) {
          // Формула расчёта доступна любому игроку, независимо от владельца
          // юнита и от того, чей сейчас ход (по образцу ближнего боя).
          // Явная проверка фракции обязательна - hasUsedAbilityThisWarPhase
          // общий флаг для всех фракций со способностями (см. configs/units.js)
          content = <SelectedUnitSeduction/>;
        } else if (selectedUnit && isUnitFaction(selectedUnit, FACTIONS.GOBLINS) && selectedUnit.hasUsedAbilityThisWarPhase) {
          // Тот же принцип, что и у соблазнения выше - формула применённой
          // способности доступна любому игроку, независимо от владельца
          // юнита и от того, чей сейчас ход (design.md Decision 25).
          // Проверяется ДО checkIfGoblinsUnitCouldUseAbility ниже - тот и так
          // вернёт false для уже применившего способность отряда
          // (hasUsedAbilityThisWarPhase проверяется внутри каждой из трёх
          // calculateAvailable*Cells), порядок здесь только для ясности
          content = <SelectedUnitGoblinsAbility/>;
        } else if (isCurrentUserTurn
          && selectedUnit && isUnitFaction(selectedUnit, FACTIONS.SUCCUBES)
          && checkIfSuccubesUnitCouldSeduce(
            selectedUnit, units, currentPlayer.coins, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
          )
        ) {
          content = <SuccubesAbilitiesToolbar/>
        } else if (isCurrentUserTurn
          && selectedUnit && isUnitFaction(selectedUnit, FACTIONS.GOBLINS)
          && checkIfGoblinsUnitCouldUseAbility(
            selectedUnit, units, currentPlayer.coins, hexes, citiesCache, specialBuildings, buildingsDamage,
            goblinsTraps, currentPlayer, fortressesUnderConstruction
          )
        ) {
          content = <GoblinsAbilitiesToolbar/>
        }
        break;
      case WAR_PHASE_STAGES.SHOOTING:
        if (selectedUnit) {
          const shootingAction = pendingShootingActions.find(action => action.shooterUnitId === selectedUnit.id);
          // Пока залп летит, панель не открывается: она закрыла бы низ поля,
          // где нередко стоит отряд-цель
          if (shootingAction && isShootingResultRevealed) {
            content = <SelectedUnitShooting/>;
          }
        }
        break;
      case WAR_PHASE_STAGES.COMBAT:
        if (selectedUnit && !showMeleeTargets) {
          const meleeAction = pendingMeleeDamage.find(action => action.unitId === selectedUnit.id);
          // Пока на поле идут удары, панель не открывается: она показала бы
          // итог боя раньше плашек отрядов и закрыла бы низ поля
          if (meleeAction && isMeleeResultRevealed) {
            content = <SelectedUnitMelee/>;
          }
        }
        break;
      default:
        break
    }
  }

  const isEmpty = !content;

  useEffect(() => {
    setIsContentEmpty(isEmpty)
  }, [setIsContentEmpty, isEmpty]);

  if (isEmpty) {
    return null;
  }

  return (
    <div className={'footer-content'}>
      {content}
    </div>
  );
}

export default TokensToolbar;